import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {Button, Icon, Message, Modal, Progress, Table} from "semantic-ui-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import DiffViewer from "react-diff-viewer";
import {FileViewer} from "../../../../util/fileViewer/FileViewer";

export type RepeatRunState = "RUNNING" | "DONE" | "ERROR"

export interface RepeatRun {
    nr: number
    state: RepeatRunState
    xRequestId?: string
    output?: string
    error?: string
    durationMs?: number
}

export interface ImportSimRepeatRunsModalProps {
    isOpen: boolean
    onClose: () => void
    fileName: string | null
    inputFile: File | null
    runs: RepeatRun[]
    running: boolean
}

const IGNORED_KEYS = new Set(["logs", "attachments"])

const MODAL_HEIGHT = "92vh"
const HEADER_HEIGHT = "66px"

function strip(value: any): any {
    if (Array.isArray(value)) return value.map(strip)
    if (value && typeof value === "object") {
        const out: any = {}
        Object.keys(value).forEach(key => {
            if (IGNORED_KEYS.has(key)) return
            out[key] = strip(value[key])
        })
        return out
    }
    return value
}

function comparableJson(raw?: string): string {
    if (!raw) return ""
    try {
        return JSON.stringify(strip(JSON.parse(raw)), null, 2)
    } catch (e) {
        return raw
    }
}

export function ImportSimRepeatRunsModal(props: ImportSimRepeatRunsModalProps) {

    const {runs, running, fileName, inputFile} = props

    const [runA, setRunA] = useState<number | null>(null)
    const [runB, setRunB] = useState<number | null>(null)
    const [aceView, setAceView] = useState<boolean>(false)
    const [showInput, setShowInput] = useState<boolean>(false)
    const [inputBuffer, setInputBuffer] = useState<ArrayBuffer | null>(null)

    const doneNrs = runs.filter(r => r.state === "DONE").map(r => r.nr)
    const finished = runs.filter(r => r.state !== "RUNNING").length
    const expanded = !running && doneNrs.length > 0

    // preselect the first two finished runs / reset on a new batch
    useEffect(() => {
        if (doneNrs.length === 0) {
            setRunA(null)
            setRunB(null)
            return
        }
        const a = (runA != null && doneNrs.includes(runA)) ? runA : doneNrs[0]
        if (a !== runA) setRunA(a)
        if (runB == null || !doneNrs.includes(runB) || runB === a) {
            setRunB(doneNrs.find(nr => nr !== a) ?? null)
        }
    }, [runs]);

    // input file is read once, lazily, and only when the preview is opened
    useEffect(() => {
        setInputBuffer(null)
    }, [inputFile]);

    useEffect(() => {
        if (!showInput || !inputFile || inputBuffer) return
        const reader = new FileReader()
        reader.onload = e => setInputBuffer(e.target.result as ArrayBuffer)
        reader.onerror = () => setInputBuffer(null)
        reader.readAsArrayBuffer(inputFile)
    }, [showInput, inputFile]);

    /** memoized -> selecting runs / switching views does not re-render the file viewer */
    const inputViewer = useMemo(
        () => (inputBuffer && fileName)
            ? <FileViewer fileBuffer={inputBuffer} fileName={fileName} displayName={false}/>
            : <div style={{padding: "10px", color: "#888"}}>loading…</div>,
        [inputBuffer, fileName]
    )

    const outputA = comparableJson(runs.find(r => r.nr === runA)?.output)
    const outputB = comparableJson(runs.find(r => r.nr === runB)?.output)

    function drawStateIcon(run: RepeatRun) {
        switch (run.state) {
            case "RUNNING":
                return <Icon name={"spinner"} loading color={"grey"}/>
            case "DONE":
                return <Icon name={"check"} color={"green"}/>
            case "ERROR":
                return <Icon name={"times"} color={"red"} title={run.error}/>
        }
    }

    function selectButton(run: RepeatRun, selected: boolean, onSelect: () => void) {
        return <Button
            circular
            compact
            size={"mini"}
            icon={selected ? "dot circle outline" : "circle outline"}
            color={selected ? "blue" : undefined}
            disabled={run.state !== "DONE"}
            onClick={onSelect}
        />
    }

    function drawRunTable() {
        return <Table compact basic={"very"} size={"small"} style={{margin: 0}}>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell width={1}>Run</Table.HeaderCell>
                    <Table.HeaderCell width={1}/>
                    <Table.HeaderCell width={2}>Duration</Table.HeaderCell>
                    <Table.HeaderCell width={7}>x-request-id</Table.HeaderCell>
                    <Table.HeaderCell width={1} textAlign={"center"}>Left</Table.HeaderCell>
                    <Table.HeaderCell width={1} textAlign={"center"}>Right</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {runs.map(run => <Table.Row key={run.nr}>
                    <Table.Cell>#{run.nr}</Table.Cell>
                    <Table.Cell>{drawStateIcon(run)}</Table.Cell>
                    <Table.Cell>{run.durationMs ? (run.durationMs / 1000).toFixed(1) + " s" : "-"}</Table.Cell>
                    <Table.Cell>
                        {run.state === "ERROR"
                            ? <span style={{color: "#db2828"}}>{run.error}</span>
                            : <>
                                <code style={{fontSize: "12px"}}>{run.xRequestId ?? "-"}</code>
                                {run.xRequestId && <Button
                                    icon={"copy"} size={"mini"} basic compact
                                    title={"copy x-request-id"} style={{marginLeft: "6px"}}
                                    onClick={() => navigator.clipboard?.writeText(run.xRequestId)}
                                />}
                            </>}
                    </Table.Cell>
                    <Table.Cell textAlign={"center"}>
                        {selectButton(run, runA === run.nr, () => setRunA(run.nr))}
                    </Table.Cell>
                    <Table.Cell textAlign={"center"}>
                        {selectButton(run, runB === run.nr, () => setRunB(run.nr))}
                    </Table.Cell>
                </Table.Row>)}
            </Table.Body>
        </Table>
    }

    function drawPaneTitles() {
        return <div style={{
            flex: "0 0 auto",
            display: "flex",
            fontSize: "12px",
            fontWeight: 600,
            color: "#555",
            padding: "2px 0"
        }}>
            <div style={{flex: 1}}>Run #{runA}</div>
            <div style={{flex: 1}}>Run #{runB}</div>
        </div>
    }

    function drawComparison() {
        if (runA == null || runB == null) {
            return <Message info content={"Select two finished runs (left / right) to compare them."}/>
        }

        if (aceView) {
            return <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
                {drawPaneTitles()}
                <div style={{flex: "1 1 auto", display: "flex", gap: "6px", minHeight: 0}}>
                    <div style={{flex: 1, minWidth: 0, height: "100%"}}>
                        <AceEditor mode={"json"} theme={"monokai"} value={outputA} readOnly
                                   width={"100%"} height={"100%"} fontSize={12} showPrintMargin={false}
                                   setOptions={{useWorker: false, showLineNumbers: true, tabSize: 2}}/>
                    </div>
                    <div style={{flex: 1, minWidth: 0, height: "100%"}}>
                        <AceEditor mode={"json"} theme={"monokai"} value={outputB} readOnly
                                   width={"100%"} height={"100%"} fontSize={12} showPrintMargin={false}
                                   setOptions={{useWorker: false, showLineNumbers: true, tabSize: 2}}/>
                    </div>
                </div>
            </div>
        }

        return <div style={{fontSize: "12px"}}>
            {drawPaneTitles()}
            <DiffViewer
                oldValue={outputA}
                newValue={outputB}
                splitView={true}
            />
        </div>
    }

    return <Modal
        open={props.isOpen}
        onClose={props.onClose}
        closeIcon
        size={expanded ? "fullscreen" : "small"}
        style={expanded ? {width: "96vw", height: MODAL_HEIGHT} : undefined}
    >
        <Modal.Header>
            Repeat run – {fileName}
            {expanded && <span style={{float: "right", marginRight: "35px"}}>
                <Button
                    size={"tiny"}
                    basic={!showInput}
                    icon={"file outline"}
                    content={showInput ? "Hide input file" : "Show input file"}
                    disabled={inputFile == null}
                    onClick={() => setShowInput(!showInput)}
                    style={{marginRight: "10px"}}
                />
                <Button.Group size={"tiny"}>
                    <Button active={!aceView} onClick={() => setAceView(false)} content={"Diff"}/>
                    <Button active={aceView} onClick={() => setAceView(true)} content={"Side by side AceEditor"}/>
                </Button.Group>
            </span>}
        </Modal.Header>

        <Modal.Content style={expanded
            ? {
                height: `calc(${MODAL_HEIGHT} - ${HEADER_HEIGHT})`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                padding: "10px 14px"
            }
            : {maxHeight: "70vh", overflow: "auto", padding: "10px 14px"}}>

            {running && <Progress
                value={finished} total={runs.length} progress={"ratio"}
                size={"small"} color={"blue"} active
                content={"running with disabled AI cache…"}
                style={{flex: "0 0 auto", marginBottom: "8px"}}
            />}

            <div style={{flex: "0 0 auto"}}>
                {drawRunTable()}
            </div>

            {!running && doneNrs.length === 0 && <Message
                negative
                header={"All runs failed"}
                content={runs.find(r => r.error)?.error ?? "no result was returned"}
            />}

            {expanded && <>
                <div style={{flex: "0 0 auto", fontSize: "12px", color: "#888", margin: "6px 0"}}>
                    logs and attachments are excluded from the comparison
                    {outputA && outputA === outputB && <b style={{marginLeft: "8px"}}>– both runs are identical</b>}
                </div>

                <div style={{flex: "1 1 auto", display: "flex", minHeight: 0, gap: "8px"}}>
                    {showInput && <div style={{
                        flex: "0 0 32%",
                        minWidth: 0,
                        height: "100%",
                        overflow: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px"
                    }}>
                        {inputViewer}
                    </div>}
                    <div style={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        height: "100%",
                        overflow: aceView ? "hidden" : "auto"
                    }}>
                        {drawComparison()}
                    </div>
                </div>
            </>}

        </Modal.Content>
    </Modal>
}